import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../../components/DataTable/DataTable';
import { useCatalogue, useSaveCatalogueItem } from '../../hooks';
import type { CatalogueKind, ICatalogueItem, PricingModel } from '../../types';
import { KIND_LABELS, PRICING_LABELS, PRICING_SHORT, money } from './pricing';
import styles from './Catalogue.module.scss';

const MODELS = Object.keys(PRICING_LABELS) as PricingModel[];
const KINDS = Object.keys(KIND_LABELS) as CatalogueKind[];

const Editor = ({
  item,
  onClose,
  onSave,
  saving,
}: {
  item: ICatalogueItem;
  onClose: () => void;
  onSave: (next: ICatalogueItem) => void;
  saving: boolean;
}) => {
  const [draft, setDraft] = useState<ICatalogueItem>(item);
  useEffect(() => setDraft(item), [item]);
  const set = <K extends keyof ICatalogueItem>(key: K, value: ICatalogueItem[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <aside className={styles.editor} aria-label={`Edit ${item.name}`}>
      <header className={styles.editorHeader}>
        <div>
          <p className={styles.eyebrow}>
            {item.source === 'marketplace' ? 'Marketplace add-on' : KIND_LABELS[item.kind]}
          </p>
          <h2 className={styles.editorTitle}>{item.name}</h2>
          <code className={styles.code}>{item.code}</code>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          onSave(draft);
        }}
      >
        <label className={styles.field}>
          <span>Name</span>
          <input
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            required
          />
        </label>
        <label className={styles.field}>
          <span>Description</span>
          <textarea
            value={draft.description ?? ''}
            onChange={(event) => set('description', event.target.value || null)}
            rows={2}
          />
        </label>
        <label className={styles.field}>
          <span>Kind</span>
          <select
            value={draft.kind}
            onChange={(event) => set('kind', event.target.value as CatalogueKind)}
            disabled={item.source === 'marketplace'}
          >
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>Pricing model</span>
          <select
            value={draft.pricing_model}
            onChange={(event) => set('pricing_model', event.target.value as PricingModel)}
          >
            {MODELS.map((model) => (
              <option key={model} value={model}>
                {PRICING_LABELS[model]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>Default price (£)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={draft.default_price ?? ''}
            placeholder="Not priced yet"
            onChange={(event) =>
              set('default_price', event.target.value === '' ? null : Number(event.target.value))
            }
          />
          <small>
            {draft.default_price !== null
              ? `${money(draft.default_price)} ${PRICING_SHORT[draft.pricing_model]}`
              : 'Tenants cannot be subscribed until this is set.'}
          </small>
        </label>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(event) => set('active', event.target.checked)}
          />
          Available to sell
        </label>
        <div className={styles.formActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
      <dl className={styles.facts}>
        <dt>Installed by</dt>
        <dd>{item.addon_code ? `${item.installed_by} tenants` : 'n/a'}</dd>
        <dt>Subscribed by</dt>
        <dd>{item.subscribed_by} tenants</dd>
      </dl>
    </aside>
  );
};

export const Catalogue = () => {
  const { data: items = [], isLoading, error } = useCatalogue();
  const save = useSaveCatalogueItem();
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const current = items.find((item) => item.code === selected) ?? null;

  const unpriced = items.filter((item) => item.default_price === null).length;

  const columns = useMemo<ColumnDef<ICatalogueItem, any>[]>(
    () => [
      {
        id: 'product',
        header: 'Product',
        accessorFn: (row) => `${row.name} ${row.code}`,
        size: 280,
        cell: ({ row }) => (
          <div className={styles.product}>
            <span className={styles.badge} data-kind={row.original.kind}>
              {KIND_LABELS[row.original.kind]}
            </span>
            <div className={styles.productText}>
              <span className={styles.productName}>{row.original.name}</span>
              <span className={styles.productCode}>{row.original.code}</span>
            </div>
          </div>
        ),
      },
      {
        header: 'Source',
        accessorKey: 'source',
        size: 120,
        cell: ({ getValue }) => (getValue<string>() === 'marketplace' ? 'Marketplace' : 'Manual'),
      },
      {
        header: 'Pricing',
        accessorKey: 'pricing_model',
        size: 170,
        cell: ({ getValue }) => PRICING_LABELS[getValue<PricingModel>()],
      },
      {
        header: 'Default price',
        accessorKey: 'default_price',
        size: 150,
        meta: { align: 'right' },
        cell: ({ row }) =>
          row.original.default_price === null ? (
            <span className={styles.unpriced}>not priced</span>
          ) : (
            <span>
              {money(row.original.default_price)}{' '}
              <span className={styles.muted}>{PRICING_SHORT[row.original.pricing_model]}</span>
            </span>
          ),
      },
      {
        header: 'Installed',
        accessorKey: 'installed_by',
        size: 100,
        meta: { align: 'right' },
        cell: ({ row }) => (row.original.addon_code ? row.original.installed_by : '—'),
      },
      { header: 'Subscribed', accessorKey: 'subscribed_by', size: 110, meta: { align: 'right' } },
      {
        header: 'Available',
        accessorKey: 'active',
        size: 100,
        cell: ({ getValue }) => (getValue<boolean>() ? 'Yes' : 'No'),
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Catalogue</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? 'Loading…'
              : `${items.length} products. Add-ons come from the marketplace; ${unpriced} still need a price.`}
          </p>
        </div>
        <input
          className={styles.search}
          type="search"
          placeholder="Search products"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search products"
        />
      </header>
      <div className={current ? styles.split : undefined}>
        {error ? (
          <p className={styles.error}>
            Could not load the catalogue:{' '}
            {(error as { message?: string }).message ?? 'unknown error'}
          </p>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            storageKey="nexus-ui.catalogue.table"
            globalFilter={search}
            onRowClick={(row) => setSelected(row.code)}
            emptyMessage={isLoading ? 'Loading catalogue…' : 'No products match.'}
          />
        )}
        {current ? (
          <Editor
            item={current}
            saving={save.isLoading}
            onClose={() => setSelected(null)}
            onSave={(next) => save.mutate(next, { onSuccess: () => setSelected(null) })}
          />
        ) : null}
      </div>
    </div>
  );
};
